import React, { useState, useEffect } from 'react';
import * as api from '@/api/client';
import type { Matter } from '@/types';
import { useAppStore } from '@/store';
import {
  Plus, Scale, Clock, ArrowRight, Loader2, AlertTriangle, Trash2, LogOut, FolderKanban,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import SettingsMenu from '@/components/layout/SettingsMenu';
import AppFooter from '@/components/layout/AppFooter';

// Strip provider prefixes + ":free" route suffixes off OpenRouter-style
// model ids so "anthropic/claude-sonnet-4-6:free" renders as
// "claude-sonnet-4-6" on the status line. Mirrors the compact form the
// SettingsMenu popup already uses so the two surfaces agree.
const shortModelName = (full: string | undefined | null): string => {
  if (!full) return '—';
  const after = full.includes('/') ? full.split('/').slice(1).join('/') : full;
  return after.split(':')[0];
};

interface HomeProps {
  onOpenSettings?: () => void;
  onOpenPrompts?: () => void;
  onOpenSkill?: () => void;
  onOpenDiagnostic?: () => void;
  onOpenStageJobs?: () => void;
  onOpenUsers?: () => void;
  onOpenMatters?: () => void;
  onLogout?: () => void;
  currentUser?: { email: string; role: string } | null;
}

export default function Home({ onOpenSettings, onOpenPrompts, onOpenSkill, onOpenDiagnostic, onOpenStageJobs, onOpenUsers, onOpenMatters, onLogout, currentUser }: HomeProps) {
  const { setMatterId, setMatterState, setActiveStage } = useAppStore();
  const [matters, setMatters] = useState<Matter[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState('');
  const [jurisdiction, setJurisdiction] = useState<string>('EP');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Active-models summary shown as a subtle one-liner under the
  // subtitle — Draft / Fast / Vision are the three models that
  // directly affect drafter-facing output (internal pipeline models
  // Tree / QC / Provenance / PageIndex live behind the gear popup).
  const [activeModels, setActiveModels] = useState<api.ActiveModels | null>(null);
  // Matter-management fields. docket_id is auto-generated server-side
  // when blank; every other optional field is TEXT/DATE on the matter
  // row. Kept on a single piece of state so the payload builder below
  // can trim+drop empty strings in one pass.
  const [clientName, setClientName] = useState('');
  const [docketId, setDocketId] = useState('');
  const [clientRef, setClientRef] = useState('');
  const [inventionTitle, setInventionTitle] = useState('');
  const [technologyArea, setTechnologyArea] = useState('');
  const [filingDeadline, setFilingDeadline] = useState('');
  const [priorityDate, setPriorityDate] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [optionalOpen, setOptionalOpen] = useState(false);
  // Two-click delete: first click arms the matter id; second click within the
  // same button-render actually deletes. This gives the drafter a clear "are
  // you sure?" without a full modal — the icon swaps to a red confirm state.
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadMatters();
    // One-shot active-models fetch for the status line below the
    // subtitle. Silently falls back to null so a network hiccup just
    // hides the line rather than breaking the page.
    api.getActiveModels()
      .then((m) => setActiveModels(m))
      .catch(() => setActiveModels(null));
  }, []);

  const loadMatters = async () => {
    try {
      const data = await api.listMatters();
      setMatters(data.matters || []);
    } catch (e) {
      console.error('Failed to load matters', e);
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!title.trim()) return;
    setCreating(true);
    setError(null);
    try {
      // Build the payload from the matter-management fields. Only send
      // keys whose value is non-empty so the backend's default / auto-
      // generation branches kick in (e.g. docket_id auto-generation
      // when blank). Title + jurisdiction are always required.
      const payload: api.MatterCreatePayload = {
        title: title.trim(),
        jurisdiction,
      };
      const maybe: Array<[keyof api.MatterCreatePayload, string]> = [
        ['docket_id', docketId],
        ['client_name', clientName],
        ['client_reference', clientRef],
        ['invention_title', inventionTitle],
        ['technology_area', technologyArea],
        ['tags', tagsInput],
        ['filing_deadline', filingDeadline],
        ['priority_date', priorityDate],
      ];
      for (const [k, v] of maybe) {
        const trimmed = v.trim();
        if (trimmed) (payload as any)[k] = trimmed;
      }
      const matter = await api.createMatter(payload);
      const fullState = await api.getMatter(matter.id);
      setMatterId(matter.id);
      setMatterState(fullState);
      // Explicit land-on-current_stage for a freshly created matter.
      // setMatterState no longer syncs activeStage (so in-stage
      // refreshes don't yank the drafter backwards).
      setActiveStage(fullState.matter.current_stage);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to create matter');
    }
    setCreating(false);
  };

  const handleOpenMatter = async (id: string) => {
    try {
      const fullState = await api.getMatter(id);
      setMatterId(id);
      setMatterState(fullState);
      setActiveStage(fullState.matter.current_stage);
      const m = fullState?.matter;
      api.postEvent({
        event_type: 'matter_opened',
        event_category: 'navigation',
        matter_id: id,
        element_id: 'matter_card_open_btn',
        event_data: {
          docket_id: m?.docket_id || null,
          status: m?.status || null,
          claim_count: (fullState?.claims || []).length,
        },
      });
    } catch (e: any) {
      setError('Failed to open matter');
    }
  };

  const handleDelete = async (id: string, ev: React.MouseEvent) => {
    ev.stopPropagation();
    // First tap arms; second tap (while armed) fires the delete.
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      return;
    }
    setDeletingId(id);
    setError(null);
    try {
      await api.deleteMatter(id);
      setMatters(prev => prev.filter(m => m.id !== id));
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to delete matter');
    }
    setDeletingId(null);
    setConfirmDeleteId(null);
  };

  const stageLabel = (stage: string) => {
    const map: Record<string, string> = {
      stage_1: 'Stage 1',
      stage_2: 'Stage 2',
      stage_2_5: 'Stage 2.5',
      stage_3: 'Stage 3',
      stage_4: 'Stage 4',
    };
    return map[stage] || stage;
  };

  return (
    <div
      className="min-h-screen bg-white"
      style={{
        backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/2ba286b1-40d5-467c-a0a9-b746ea42943a/images/3a69a3bb912feb3516e17de3c87dc867fa1d691b62a1688b58f50b9b0548a646.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="min-h-screen bg-white/90 backdrop-blur-sm">
        {/* Minimal header */}
        <div className="border-b border-zinc-200 bg-white/80 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Scale size={22} className="text-[#002FA7]" />
              <span
                className="text-lg font-semibold tracking-tight text-zinc-900"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
              >
                Draft Studio
              </span>
            </div>
            <div className="flex items-center gap-1">
              {onOpenMatters && (
                <button
                  data-testid="home-open-matters-button"
                  onClick={() => onOpenMatters()}
                  title="All matters"
                  className="text-zinc-500 hover:text-zinc-900 transition-colors p-2 flex items-center gap-1 text-xs"
                >
                  <FolderKanban size={16} /> Matters
                </button>
              )}
              {/* Gear icon opens the shared popup — active-models summary
                  plus admin links (Prompts / Skill Studio / Diagnostic /
                  Users). The inline nav now stays minimal. */}
              <SettingsMenu
                matterId={null}
                currentUserRole={currentUser?.role || null}
                onOpenSettings={onOpenSettings}
                onOpenPrompts={onOpenPrompts}
                onOpenSkill={onOpenSkill}
                onOpenDiagnostic={onOpenDiagnostic}
                onOpenStageJobs={onOpenStageJobs}
                onOpenUsers={onOpenUsers}
              />
              {onLogout && (
                <button
                  data-testid="home-logout-button"
                  onClick={() => onLogout()}
                  title={currentUser?.email ? `Sign out ${currentUser.email}` : 'Sign out'}
                  className="text-zinc-500 hover:text-zinc-900 transition-colors p-2"
                >
                  <LogOut size={18} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 pt-16">
          {/* Hero */}
          <div className="mb-12">
            <h1
              data-testid="home-title"
              className="text-4xl font-semibold tracking-tight text-zinc-900 mb-3"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              Patent Claim Drafting
            </h1>
            <p className="text-lg text-zinc-500 max-w-xl" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Take an Invention Disclosure Form through structured analysis, novelty assessment, and claim generation.
            </p>
            {/* Active-models status line — subtle one-liner showing
                the three models that directly affect matter quality.
                Pipeline-internal models (Tree / QC / Provenance /
                PageIndex) live behind the gear popup so the hero
                stays uncluttered. Hidden on narrow screens to avoid
                wrapping under the subtitle. */}
            {activeModels && (
              <>
                <p
                  data-testid="home-active-models"
                  className="hidden sm:flex mt-2 text-xs text-zinc-400 font-mono items-center gap-x-3 flex-wrap max-w-2xl"
                  title="Active pipeline models — click the gear icon for the full list."
                >
                  <span>Active:</span>
                  <span>
                    Draft=
                    <span className="text-zinc-600">
                      {shortModelName(activeModels.draft_model)}
                    </span>
                  </span>
                  <span>·</span>
                  <span>
                    Fast=
                    <span className="text-zinc-600">
                      {shortModelName(activeModels.fast_model)}
                    </span>
                  </span>
                  <span>·</span>
                  <span>
                    Vision=
                    <span className="text-zinc-600">
                      {shortModelName(activeModels.vision_model)}
                    </span>
                  </span>
                </p>
                {/* Compliance signal — sibling metadata line below
                    the Active-models line. Reads as metadata, not
                    marketing: same muted zinc-400, same text-xs,
                    same font-mono, same sm-only visibility so the
                    narrow-viewport layout stays identical to the
                    pre-addition shape. " · " separator matches the
                    Active line. Plain text per the addendum spec —
                    no icons, badges, emoji, or tooltip. */}
                <p
                  data-testid="home-epi-tagline"
                  className="hidden sm:flex mt-1 text-xs text-zinc-400 font-mono items-center gap-x-3 flex-wrap max-w-2xl"
                >
                  <span>epi-aligned</span>
                  <span>·</span>
                  <span>governance-ready</span>
                </p>
              </>
            )}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-center gap-2">
              <AlertTriangle size={16} />
              {error}
            </div>
          )}

          {/* New matter */}
          {!showNew ? (
            <button
              data-testid="new-matter-button"
              onClick={() => setShowNew(true)}
              className="mb-10 bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-5 py-3 font-medium transition-colors flex items-center gap-2"
            >
              <Plus size={16} />
              New Matter
            </button>
          ) : (
            <div className="mb-10 border border-zinc-200 rounded-sm bg-white p-6 max-w-xl" data-testid="new-matter-form">
              <h3 className="text-sm font-medium text-zinc-900 mb-4">Create New Matter</h3>
              <div className="space-y-4">
                {/* Required fields — title + client name + jurisdiction. */}
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Matter Title *</label>
                  <input
                    data-testid="matter-title-input"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Autonomous Navigation System"
                    className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Client Name</label>
                  <input
                    data-testid="matter-client-name-input"
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="e.g., Acme Robotics Ltd."
                    className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Jurisdiction *</label>
                  <div className="flex gap-2">
                    {['EP', 'US', 'IN'].map(j => (
                      <button
                        key={j}
                        data-testid={`jurisdiction-${j}`}
                        onClick={() => setJurisdiction(j)}
                        className={`flex-1 px-4 py-2 rounded-sm text-sm font-medium border transition-colors ${
                          jurisdiction === j
                            ? 'border-[#002FA7] bg-blue-50 text-[#002FA7]'
                            : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300'
                        }`}
                      >
                        {j}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Optional details — collapsed by default so the form
                    stays simple. Each field maps 1:1 onto a column on
                    the matter row; leaving them blank just omits the
                    key from the POST payload so the backend falls back
                    to its default / auto-generation. */}
                <div className="border-t border-zinc-100 pt-3">
                  <button
                    type="button"
                    data-testid="matter-optional-toggle"
                    onClick={() => setOptionalOpen(v => !v)}
                    aria-expanded={optionalOpen}
                    className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900"
                  >
                    {optionalOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    Optional details
                  </button>
                  {optionalOpen && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="matter-optional-fields">
                      <div>
                        <label className="text-xs text-zinc-500 block mb-1">Docket ID</label>
                        <input
                          data-testid="matter-docket-id-input"
                          type="text"
                          value={docketId}
                          onChange={(e) => setDocketId(e.target.value)}
                          placeholder="Leave blank to auto-generate (e.g. ARC-2026-001)"
                          className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                        />
                        <p className="text-[10px] text-zinc-400 mt-1">Auto-generated as PREFIX-YEAR-NNN</p>
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500 block mb-1">Client Reference</label>
                        <input
                          data-testid="matter-client-ref-input"
                          type="text"
                          value={clientRef}
                          onChange={(e) => setClientRef(e.target.value)}
                          placeholder="Client's own reference number"
                          className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="text-xs text-zinc-500 block mb-1">Invention Title</label>
                        <input
                          data-testid="matter-invention-title-input"
                          type="text"
                          value={inventionTitle}
                          onChange={(e) => setInventionTitle(e.target.value)}
                          className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500 block mb-1">Technology Area</label>
                        <select
                          data-testid="matter-technology-area-input"
                          value={technologyArea}
                          onChange={(e) => setTechnologyArea(e.target.value)}
                          className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                        >
                          <option value="">—</option>
                          <option value="Software">Software</option>
                          <option value="MedTech">MedTech</option>
                          <option value="CleanTech">CleanTech</option>
                          <option value="Electronics">Electronics</option>
                          <option value="Mechanical">Mechanical</option>
                          <option value="Biotech">Biotech</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500 block mb-1">Tags</label>
                        <input
                          data-testid="matter-tags-input"
                          type="text"
                          value={tagsInput}
                          onChange={(e) => setTagsInput(e.target.value)}
                          placeholder="comma-separated"
                          className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500 block mb-1">Filing Deadline</label>
                        <input
                          data-testid="matter-filing-deadline-input"
                          type="date"
                          value={filingDeadline}
                          onChange={(e) => setFilingDeadline(e.target.value)}
                          className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500 block mb-1">Priority Date</label>
                        <input
                          data-testid="matter-priority-date-input"
                          type="date"
                          value={priorityDate}
                          onChange={(e) => setPriorityDate(e.target.value)}
                          className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    data-testid="create-matter-submit"
                    onClick={handleCreate}
                    disabled={creating || !title.trim()}
                    className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {creating ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                    Create
                  </button>
                  <button
                    onClick={() => setShowNew(false)}
                    className="bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-4 py-2 text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Recent matters */}
          <div>
            <h2
              className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4"
            >
              Recent Matters
            </h2>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-zinc-500">
                <Loader2 size={14} className="animate-spin" />
                Loading...
              </div>
            ) : matters.length === 0 ? (
              <p className="text-sm text-zinc-400">No matters yet. Create one to get started.</p>
            ) : (
              <div className="space-y-2">
                {matters.map(m => {
                  const armed = confirmDeleteId === m.id;
                  const deleting = deletingId === m.id;
                  return (
                    <div
                      key={m.id}
                      data-testid={`matter-${m.id}`}
                      className="w-full border border-zinc-200 rounded-sm bg-white p-4 hover:border-zinc-300 hover:bg-zinc-50/50 transition-colors flex items-center justify-between group"
                    >
                      <button
                        type="button"
                        onClick={() => handleOpenMatter(m.id)}
                        className="flex-1 text-left"
                      >
                        <span className="text-sm font-medium text-zinc-900">{m.title}</span>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="px-2 py-0.5 bg-zinc-100 border border-zinc-200 rounded-sm text-xs text-zinc-600">
                            {m.jurisdiction}
                          </span>
                          <span className="text-xs text-zinc-400">{stageLabel(m.current_stage)}</span>
                          <span className="text-xs text-zinc-400">
                            <Clock size={10} className="inline mr-1" />
                            {new Date(m.updated_at).toLocaleDateString()}
                          </span>
                        </div>
                      </button>
                      <div className="flex items-center gap-2 ml-3">
                        <button
                          type="button"
                          data-testid={`delete-matter-${m.id}`}
                          onClick={(e) => handleDelete(m.id, e)}
                          onBlur={() => setConfirmDeleteId(cur => (cur === m.id ? null : cur))}
                          disabled={deleting}
                          title={armed ? 'Click again to confirm' : 'Delete matter'}
                          className={`p-2 rounded-sm text-xs transition-colors flex items-center gap-1 ${
                            armed
                              ? 'bg-red-600 text-white hover:bg-red-700'
                              : 'text-zinc-400 hover:text-red-600 hover:bg-red-50'
                          }`}
                        >
                          {deleting
                            ? <Loader2 size={14} className="animate-spin" />
                            : <Trash2 size={14} />}
                          {armed && !deleting && <span className="pr-1">Confirm</span>}
                        </button>
                        <ArrowRight size={16} className="text-zinc-300 group-hover:text-[#002FA7] transition-colors" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <AppFooter className="mt-8" />
      </div>
    </div>
  );
}
